import { useEffect, useState, useRef } from 'react';
import serverErrorHandler from 'services/serverError.service';
import {
  getFormValidationStatusAsync,
  scrollToFormErr,
} from 'utils/form.util';
import { FormField } from 'components/Form/CustomForm';
import {
  openErrorToaster,
  openSuccessToaster,
} from 'services/toast.service';
import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import verifyInvite from '../api/verifyInvite';
import setPasswordFromInvite from '../api/setPasswordFromInvite';
import { authPasswordWithConfirmFormModel as model } from '../validation/schemas';

export interface ISetPasswordForm {
  password: string;
  passwordConfirm: string;
}

const INIT_FORM = {
  password: '',
  passwordConfirm: '',
};

const fields: FormField[] = [
  {
    name: 'password',
    type: 'password',
    label: 'Password',
    size: 'lg',
    data: null,
  },
  {
    name: 'passwordConfirm',
    type: 'password',
    label: 'Confirm Password',
    size: 'lg',
    data: null,
  },
];

export default function useSetPasswordFromInvite(token: string) {
  const [formValue, setFormValue] = useState<ISetPasswordForm>(INIT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const hasVerifiedRef = useRef(false);

  // Verify token on mount
  useEffect(() => {
    // Prevent multiple verification attempts (React Strict Mode double-invoke)
    if (hasVerifiedRef.current) {
      return;
    }

    async function verifyTokenAsync() {
      try {
        setIsVerifyingToken(true);
        setErrorMessage(null);
        hasVerifiedRef.current = true;
        const response = await verifyInvite(token || '');

        if (response.valid && response.user) {
          setIsTokenValid(true);
          setUserInfo(response.user);
        } else {
          setIsTokenValid(false);
          const errorMsg = 'Invalid or expired invite link';
          setErrorMessage(errorMsg);
          openErrorToaster(errorMsg, 5000);
        }
      } catch (e: any) {
        setIsTokenValid(false);
        let errorMsg =
          'Unable to verify invite link. Please try again later.';

        if (
          e.code === 'ERR_NETWORK' ||
          e.message?.includes('ERR_CONNECTION_REFUSED') ||
          !e.response
        ) {
          errorMsg =
            'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (e.response) {
          const err = e.response;
          if (err.data?.message) {
            if (
              err.data.message.includes('expired') ||
              err.data.message.includes('Invalid')
            ) {
              errorMsg =
                'This invite link has expired or is invalid. Please contact your administrator for a new invite.';
            } else {
              errorMsg = err.data.message;
            }
          } else {
            errorMsg =
              'An error occurred while verifying the invite link. Please try again later.';
          }
        }

        setErrorMessage(errorMsg);
        openErrorToaster(errorMsg, 5000);
      } finally {
        setIsVerifyingToken(false);
      }
    }

    if (token) {
      verifyTokenAsync();
    } else {
      setIsTokenValid(false);
      setIsVerifyingToken(false);
      hasVerifiedRef.current = true;
      const errorMsg =
        'Missing invite token. Please use a valid invite link.';
      setErrorMessage(errorMsg);
      openErrorToaster(errorMsg, 5000);
    }
  }, [token]);

  /**
   * Handles password set submission.
   * On success, automatically signs in the user via Firebase.
   */
  const onSubmit = async () => {
    try {
      const isValid = await getFormValidationStatusAsync(
        model.check(formValue),
      );

      if (isValid && userInfo?.email) {
        setIsLoading(true);

        await setPasswordFromInvite({
          token,
          password: formValue.password,
        });

        // Automatically sign in the user with Firebase
        const auth = getAuth();
        await setPersistence(auth, browserLocalPersistence);
        await signInWithEmailAndPassword(
          auth,
          userInfo.email,
          formValue.password,
        );

        // User will be automatically redirected via useAuth hook
        openSuccessToaster('Password set successfully. Welcome!', 3500);
        setIsLoading(false);
      } else if (!userInfo?.email) {
        openErrorToaster(
          'Unable to retrieve user information. Please try again.',
          5000,
        );
        setIsLoading(false);
      } else {
        scrollToFormErr();
      }
    } catch (e) {
      setIsLoading(false);
      serverErrorHandler(e);
    }
  };

  const onChange = (val: any) => {
    setFormValue(val);
  };

  return {
    fields,
    formValue,
    isLoading,
    isVerifyingToken,
    isTokenValid,
    errorMessage,
    userInfo,
    model,
    onChange,
    onSubmit,
  };
}
